// Módulo "Rampa de lançamentos": acompanhamento mensal (12 meses) de produtos já
// lançados, com metas e indicadores ROB / Volume / PCA / Margem de contribuição /
// Devolução / Preço médio. Relatório para a gestão alemã (PT/EN).
import { supabase } from './supabase';

export type RampStatus = 'acompanhando' | 'encerrado';

export interface LaunchRamp {
  id: string;
  orgId: string;
  productId: string | null;
  brandId: string | null;
  brandName: string | null;
  name: string;
  gramatura: string | null;
  codigo: string | null;
  photoUrl: string | null;
  salesStartDate: string | null;
  planMonths: number;
  metaRob: number | null;
  metaVolume: number | null;
  metaPca: number | null;
  metaMc: number | null;
  metaDevolucao: number | null;
  metaPrecoMedio: number | null;
  // Ancoragem de preço (produto da concorrência usado como referência)
  ancoraNome: string | null;
  ancoraGramatura: string | null;
  ancoraElasticidade: number | null; // % do preço do âncora (90 = 10% abaixo)
  ancoraBase: 'un' | 'kg';
  status: RampStatus;
  createdByName: string | null;
  createdAt: string;
}

export interface RampEntry {
  id: string;
  rampId: string;
  refMonth: string; // YYYY-MM-01
  rob: number | null;
  volume: number | null;
  pca: number | null;
  mc: number | null;
  devolucao: number | null;
  precoMedio: number | null;
  ancoraPreco: number | null; // preço ao consumidor do âncora no mês (R$/un.)
  comentarios: string | null;
  byName: string | null;
  createdAt: string;
}

// ---- helpers de data (iguais ao módulo de saída de linha) -------------------
export function monthKey(d: Date | string): string {
  const dt = typeof d === 'string' ? new Date(d + (d.length === 10 ? 'T00:00:00' : '')) : d;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
}
export function monthLabel(ym: string): string {
  const dt = new Date(ym + 'T00:00:00');
  const mes = dt.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return `${mes}/${String(dt.getFullYear()).slice(2)}`; // ex.: mar/26
}
/** Meses do plano a partir de uma âncora (início de vendas ou criação). */
export function rampMonthsList(anchor: string, planMonths: number): string[] {
  const start = new Date(monthKey(anchor) + 'T00:00:00');
  const out: string[] = [];
  for (let i = 0; i < Math.max(1, planMonths); i++) {
    out.push(monthKey(new Date(start.getFullYear(), start.getMonth() + i, 1)));
  }
  return out;
}

function mapRamp(r: Record<string, unknown>): LaunchRamp {
  const brand = r.brands as { name?: string } | { name?: string }[] | null;
  const brandName = Array.isArray(brand) ? brand[0]?.name ?? null : brand?.name ?? null;
  return {
    id: r.id as string,
    orgId: r.org_id as string,
    productId: (r.product_id as string) ?? null,
    brandId: (r.brand_id as string) ?? null,
    brandName,
    name: r.name as string,
    gramatura: (r.gramatura as string) ?? null,
    codigo: (r.codigo as string) ?? null,
    photoUrl: (r.photo_url as string) ?? null,
    salesStartDate: (r.sales_start_date as string) ?? null,
    planMonths: (r.plan_months as number) ?? 12,
    metaRob: (r.meta_rob as number) ?? null,
    metaVolume: (r.meta_volume as number) ?? null,
    metaPca: (r.meta_pca as number) ?? null,
    metaMc: (r.meta_mc as number) ?? null,
    metaDevolucao: (r.meta_devolucao as number) ?? null,
    metaPrecoMedio: (r.meta_preco_medio as number) ?? null,
    ancoraNome: (r.ancora_nome as string) ?? null,
    ancoraGramatura: (r.ancora_gramatura as string) ?? null,
    ancoraElasticidade: (r.ancora_elasticidade as number) ?? null,
    ancoraBase: (r.ancora_base as string) === 'kg' ? 'kg' : 'un',
    status: (r.status as RampStatus) ?? 'acompanhando',
    createdByName: (r.created_by_name as string) ?? null,
    createdAt: r.created_at as string,
  };
}
function mapEntry(r: Record<string, unknown>): RampEntry {
  return {
    id: r.id as string,
    rampId: r.ramp_id as string,
    refMonth: r.ref_month as string,
    rob: (r.rob as number) ?? null,
    volume: (r.volume as number) ?? null,
    pca: (r.pca as number) ?? null,
    mc: (r.mc as number) ?? null,
    devolucao: (r.devolucao as number) ?? null,
    precoMedio: (r.preco_medio as number) ?? null,
    ancoraPreco: (r.ancora_preco as number) ?? null,
    comentarios: (r.comentarios as string) ?? null,
    byName: (r.by_name as string) ?? null,
    createdAt: r.created_at as string,
  };
}

const RAMP_COLS =
  'id, org_id, product_id, brand_id, name, gramatura, codigo, photo_url, sales_start_date, plan_months, meta_rob, meta_volume, meta_pca, meta_mc, meta_devolucao, meta_preco_medio, ancora_nome, ancora_gramatura, ancora_elasticidade, ancora_base, status, created_by_name, created_at, brands(name)';

export async function loadRamps(orgId: string): Promise<LaunchRamp[]> {
  const { data } = await supabase
    .from('launch_ramps')
    .select(RAMP_COLS)
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });
  return ((data ?? []) as Record<string, unknown>[]).map(mapRamp);
}

export async function loadRamp(id: string): Promise<LaunchRamp | null> {
  const { data } = await supabase.from('launch_ramps').select(RAMP_COLS).eq('id', id).maybeSingle();
  return data ? mapRamp(data as Record<string, unknown>) : null;
}

export async function createRamp(params: {
  orgId: string;
  brandId: string | null;
  name: string;
  gramatura?: string | null;
  codigo?: string | null;
  photoUrl?: string | null;
  salesStartDate?: string | null;
  planMonths?: number;
  metaRob?: number | null;
  metaVolume?: number | null;
  metaPca?: number | null;
  metaMc?: number | null;
  metaDevolucao?: number | null;
  metaPrecoMedio?: number | null;
  createdById?: string | null;
  createdByName?: string | null;
}): Promise<{ error?: string; id?: string }> {
  const { data, error } = await supabase
    .from('launch_ramps')
    .insert({
      org_id: params.orgId,
      brand_id: params.brandId,
      name: params.name,
      gramatura: params.gramatura ?? null,
      codigo: params.codigo ?? null,
      photo_url: params.photoUrl ?? null,
      sales_start_date: params.salesStartDate ?? null,
      plan_months: params.planMonths ?? 12,
      meta_rob: params.metaRob ?? null,
      meta_volume: params.metaVolume ?? null,
      meta_pca: params.metaPca ?? null,
      meta_mc: params.metaMc ?? null,
      meta_devolucao: params.metaDevolucao ?? null,
      meta_preco_medio: params.metaPrecoMedio ?? null,
      created_by: params.createdById ?? null,
      created_by_name: params.createdByName ?? null,
    })
    .select('id')
    .single();
  return { error: error?.message, id: (data as { id: string } | null)?.id };
}

export async function updateRamp(id: string, patch: Record<string, unknown>): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('launch_ramps')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);
  return { error: error?.message };
}

export async function deleteRamp(id: string): Promise<{ error?: string }> {
  const { error } = await supabase.from('launch_ramps').delete().eq('id', id);
  return { error: error?.message };
}

export async function loadRampEntries(rampId: string): Promise<RampEntry[]> {
  const { data } = await supabase
    .from('launch_ramp_entries')
    .select('*')
    .eq('ramp_id', rampId)
    .order('ref_month', { ascending: true });
  return ((data ?? []) as Record<string, unknown>[]).map(mapEntry);
}

export async function upsertRampEntry(params: {
  orgId: string;
  rampId: string;
  refMonth: string;
  rob?: number | null;
  volume?: number | null;
  pca?: number | null;
  mc?: number | null;
  devolucao?: number | null;
  precoMedio?: number | null;
  ancoraPreco?: number | null;
  comentarios?: string | null;
  byId?: string | null;
  byName?: string | null;
}): Promise<{ error?: string }> {
  const { error } = await supabase.from('launch_ramp_entries').upsert(
    {
      org_id: params.orgId,
      ramp_id: params.rampId,
      ref_month: params.refMonth,
      rob: params.rob ?? null,
      volume: params.volume ?? null,
      pca: params.pca ?? null,
      mc: params.mc ?? null,
      devolucao: params.devolucao ?? null,
      preco_medio: params.precoMedio ?? null,
      ancora_preco: params.ancoraPreco ?? null,
      comentarios: params.comentarios ?? null,
      by_id: params.byId ?? null,
      by_name: params.byName ?? null,
    },
    { onConflict: 'ramp_id,ref_month' },
  );
  return { error: error?.message };
}
